/**
 * Small DOM and formatting helpers.
 *
 * Everything here sets `textContent`, never `innerHTML`, so user-supplied
 * recording labels can never become markup.
 */

type Attributes = Record<string, string | undefined>;

export const el = <K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attributes: Attributes = {},
  children: readonly (Node | string)[] = [],
): HTMLElementTagNameMap[K] => {
  const node = document.createElement(tag);
  for (const [key, value] of Object.entries(attributes)) {
    if (value === undefined) continue;
    if (key === 'class') node.className = value;
    else node.setAttribute(key, value);
  }
  for (const child of children) {
    node.append(typeof child === 'string' ? document.createTextNode(child) : child);
  }
  return node;
};

export const clear = (node: HTMLElement): void => {
  node.replaceChildren();
};

export const formatBytes = (bytes: number): string => {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 MB';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const index = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  const value = bytes / 1024 ** index;
  return `${value >= 10 || index === 0 ? Math.round(value) : value.toFixed(1)} ${units[index]}`;
};

export const formatDuration = (totalSeconds: number | null): string => {
  if (totalSeconds === null || !Number.isFinite(totalSeconds) || totalSeconds < 0) return '--:--';
  const seconds = Math.floor(totalSeconds % 60);
  const minutes = Math.floor((totalSeconds / 60) % 60);
  const hours = Math.floor(totalSeconds / 3600);
  const pad = (n: number): string => String(n).padStart(2, '0');
  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${minutes}:${pad(seconds)}`;
};

export const formatDateTime = (epochMs: number): string =>
  new Date(epochMs).toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

/** Transient message shown at the bottom of the screen. */
export const toast = (message: string, variant: 'info' | 'error' = 'info'): void => {
  const host = document.getElementById('toast-host');
  if (host === null) return;

  const node = el('div', { class: `toast toast--${variant}`, role: 'status' }, [message]);
  host.append(node);

  setTimeout(() => {
    node.classList.add('toast--leaving');
    setTimeout(() => node.remove(), 300);
  }, 3600);
};

/** Replaces a container's contents with a centred message. */
export const renderEmptyState = (
  container: HTMLElement,
  title: string,
  detail: string,
): void => {
  clear(container);
  container.append(
    el('div', { class: 'empty-state' }, [
      el('p', { class: 'empty-state__title' }, [title]),
      el('p', { class: 'empty-state__detail' }, [detail]),
    ]),
  );
};
